'use client';

import { c as _c } from "react/compiler-runtime";
import { useGetBackerRewardsSummary, useGetCreatorStats, useGetUser } from '@morfi/backend-sdk-v2/client';
import { useTranslations } from 'next-intl';
import { type JSX, useEffect } from 'react';
import { usePortfolioContext } from '@/components/portfolio/portfolio-context';
import { Skeleton } from '@/components/ui/app-skeleton';
import { Icon, type IconProps } from '@/components/ui/icon';
import { AddMoneyCircleIconSolid, AffiliateIconSolid, ArrowDataTransferHorizontalIconSolid, MoneyExchange01IconSolid, Rocket01IconSolid } from '@/components/ui/icon/glyphs';
import { useFormattedCurrency } from '@/lib/currency/use-formatted-currency';
import { logger } from '@/lib/telemetry/logger';
export function ProfileActivityStats(t0) {
  const $ = _c(59);
  const {
    creatorUserId,
    walletPubkeys
  } = t0;
  const {
    userId,
    financialQueryId,
    walletPubkeys: profileWallets
  } = usePortfolioContext();
  const tMetric = useTranslations("competitions.metric");
  const tCreated = useTranslations("profile.created");
  const tStats = useTranslations("profile.stats");
  const tBacker = useTranslations("portfolio.backerRewards");
  let t1;
  if ($[0] !== tStats) {
    t1 = tStats("unavailable");
    $[0] = tStats;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const unavailable = t1;
  const {
    formatD6
  } = useFormattedCurrency();
  const t2 = walletPubkeys ?? profileWallets;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = ["totalTrades", "totalVolume"];
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== t2 || $[4] !== userId) {
    t4 = {
      identifier: userId,
      walletPubkeys: t2,
      fields: t3
    };
    $[3] = t2;
    $[4] = userId;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const t5 = userId !== "";
  let t6;
  if ($[6] !== t5) {
    t6 = {
      enabled: t5
    };
    $[6] = t5;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  const activity = useGetUser(t4, t6);
  const t7 = creatorUserId ?? "";
  let t8;
  if ($[8] !== t7) {
    t8 = {
      userId: t7
    };
    $[8] = t7;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  const t9 = creatorUserId !== null;
  let t10;
  if ($[10] !== t9) {
    t10 = {
      enabled: t9
    };
    $[10] = t9;
    $[11] = t10;
  } else {
    t10 = $[11];
  }
  const creator = useGetCreatorStats(t8, t10);
  let t11;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = ["totalEarnedD6", "claimableNowD6", "marketsBacked"];
    $[12] = t11;
  } else {
    t11 = $[12];
  }
  let t12;
  if ($[13] !== financialQueryId) {
    t12 = {
      userId: financialQueryId,
      fields: t11
    };
    $[13] = financialQueryId;
    $[14] = t12;
  } else {
    t12 = $[14];
  }
  const t13 = financialQueryId !== "";
  let t14;
  if ($[15] !== t13) {
    t14 = {
      enabled: t13
    };
    $[15] = t13;
    $[16] = t14;
  } else {
    t14 = $[16];
  }
  const backer = useGetBackerRewardsSummary(t12, t14);
  useStatFailureLog(activity.error, "activity");
  useStatFailureLog(creator.error, "creator");
  useStatFailureLog(backer.error, "backer");
  const t15 = activity.isPending ? null : activity.data?.totalTrades ?? unavailable;
  let t16;
  if ($[17] !== tMetric) {
    t16 = tMetric("trades");
    $[17] = tMetric;
    $[18] = t16;
  } else {
    t16 = $[18];
  }
  let t17;
  if ($[19] !== t15 || $[20] !== t16) {
    t17 = <ProfileActivityStat icon={MoneyExchange01IconSolid} value={t15} label={t16} />;
    $[19] = t15;
    $[20] = t16;
    $[21] = t17;
  } else {
    t17 = $[21];
  }
  let t18;
  if ($[22] !== activity.data || $[23] !== activity.isPending || $[24] !== formatD6 || $[25] !== unavailable) {
    t18 = activity.isPending ? null : activity.data ? formatD6(activity.data.totalVolume, "compactTight") : unavailable;
    $[22] = activity.data;
    $[23] = activity.isPending;
    $[24] = formatD6;
    $[25] = unavailable;
    $[26] = t18;
  } else {
    t18 = $[26];
  }
  let t19;
  if ($[27] !== tMetric) {
    t19 = tMetric("volume");
    $[27] = tMetric;
    $[28] = t19;
  } else {
    t19 = $[28];
  }
  let t20;
  if ($[29] !== t18 || $[30] !== t19) {
    t20 = <ProfileActivityStat icon={ArrowDataTransferHorizontalIconSolid} value={t18} label={t19} />;
    $[29] = t18;
    $[30] = t19;
    $[31] = t20;
  } else {
    t20 = $[31];
  }
  const t21 = creator.isPending ? null : creator.data?.marketsCreated ?? unavailable;
  let t22;
  if ($[32] !== tMetric) {
    t22 = tMetric("marketsCreated");
    $[32] = tMetric;
    $[33] = t22;
  } else {
    t22 = $[33];
  }
  let t23;
  if ($[34] !== t21 || $[35] !== t22) {
    t23 = <ProfileActivityStat icon={Rocket01IconSolid} value={t21} label={t22} />;
    $[34] = t21;
    $[35] = t22;
    $[36] = t23;
  } else {
    t23 = $[36];
  }
  const t24 = backer.isPending && financialQueryId !== "" ? null : backer.data?.marketsBacked ?? unavailable;
  let t25;
  if ($[37] !== tBacker) {
    t25 = tBacker("marketsBacked");
    $[37] = tBacker;
    $[38] = t25;
  } else {
    t25 = $[38];
  }
  let t26;
  if ($[39] !== t24 || $[40] !== t25) {
    t26 = <ProfileActivityStat icon={AddMoneyCircleIconSolid} value={t24} label={t25} />;
    $[39] = t24;
    $[40] = t25;
    $[41] = t26;
  } else {
    t26 = $[41];
  }
  let t27;
  if ($[42] !== backer.data || $[43] !== backer.isPending || $[44] !== financialQueryId || $[45] !== formatD6 || $[46] !== unavailable) {
    t27 = backer.isPending && financialQueryId !== "" ? null : backer.data ? formatD6(backer.data.totalEarnedD6, "compactTight") : unavailable;
    $[42] = backer.data;
    $[43] = backer.isPending;
    $[44] = financialQueryId;
    $[45] = formatD6;
    $[46] = unavailable;
    $[47] = t27;
  } else {
    t27 = $[47];
  }
  let t28;
  if ($[48] !== tCreated) {
    t28 = tCreated("backerRewards");
    $[48] = tCreated;
    $[49] = t28;
  } else {
    t28 = $[49];
  }
  let t29;
  if ($[50] !== t27 || $[51] !== t28) {
    t29 = <ProfileActivityStat icon={AffiliateIconSolid} value={t27} label={t28} />;
    $[50] = t27;
    $[51] = t28;
    $[52] = t29;
  } else {
    t29 = $[52];
  }
  let t30;
  if ($[53] !== t17 || $[54] !== t20 || $[55] !== t23 || $[56] !== t26 || $[57] !== t29) {
    t30 = <>{t17}{t20}{t23}{t26}{t29}</>;
    $[53] = t17;
    $[54] = t20;
    $[55] = t23;
    $[56] = t26;
    $[57] = t29;
    $[58] = t30;
  } else {
    t30 = $[58];
  }
  return t30;
}
export function ProfileActivityStat(t0) {
  const $ = _c(10);
  const {
    icon,
    value,
    label
  } = t0;
  let t1;
  if ($[0] !== icon) {
    t1 = <Icon icon={icon} className="size-4 text-foreground-tertiary" aria-hidden="true" />;
    $[0] = icon;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== value) {
    t2 = value === null ? <Skeleton className="h-5 w-8" /> : <span className="font-medium tabular-nums text-foreground-primary">{value}</span>;
    $[2] = value;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== label) {
    t3 = <span className="font-normal text-foreground-tertiary">{label}</span>;
    $[4] = label;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t1 || $[7] !== t2 || $[8] !== t3) {
    t4 = <span className="inline-flex items-center gap-1 whitespace-nowrap text-body">{t1}{t2}{t3}</span>;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
}
function useStatFailureLog(error, source) {
  const $ = _c(4);
  let t0;
  let t1;
  if ($[0] !== error || $[1] !== source) {
    t0 = () => {
      if (error === null) {
        return;
      }
      logger.warn("portfolio.profile_stats.load_failed", {
        source,
        errorName: error.name
      });
    };
    t1 = [error, source];
    $[0] = error;
    $[1] = source;
    $[2] = t0;
    $[3] = t1;
  } else {
    t0 = $[2];
    t1 = $[3];
  }
  useEffect(t0, t1);
}